import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IParentCompany } from 'types/income';
import { ContentLanguage, Currency, TableDataResponse } from 'types/general';
import { AuthProvider } from 'store/auth';
import { toCents } from 'utils/general';
import { ClaimBenefitCombination } from 'store/companies';
import { DiscountPackage } from 'store/discounts';
import {
  AdminClientsFilters,
  ClientFeature,
  DepositManagementMethod,
  UnlinkAppUsersStatus,
} from './admin-clients.types';

interface GetClients {
  filters: Partial<AdminClientsFilters>;
}

interface GetClient {
  clientId: string;
}

export interface CreateClient {
  companyName: string;
  companyCountry: string;
  companyCode: string;
  adminEmail: string;
  adminPhone: string;
  adminFirstName: string;
  adminLastName: string;
  internalComment: string;
}

interface UpdateClient {
  clientId: string;
  data: Partial<{
    name: string;
    code: string;
    defaultCurrency: Currency;
    defaultLanguage: Uppercase<ContentLanguage>;
    country: string;
    internalComment: string;
    billingInfo: string;
    accountantEmails: string[];
    accountantLanguage: Uppercase<ContentLanguage>;
    deactivationDate: string | null;
    features: ClientFeature[];
    discountPlan: DiscountPackage;
    depositManagementMethod: DepositManagementMethod;
    credit: number;
    lowDepositAlertThreshold: number;
    useExternalId: boolean;
    authProvider: AuthProvider;
    authProviderName: string;
    sendEmployeeWelcomeMessage: boolean;
    expenseCompensationBenefitCombination: ClaimBenefitCombination;
    ipWhitelistEnabled: boolean;
    ipWhitelist: string[];
    sessionTimeoutEnabled: boolean;
    sessionTimeoutInMinutes: number;
  }>;
}

interface DeleteClient {
  clientId: string;
}

interface UnlinkAppUsers {
  clientId: string;
}

export const loaders = {
  getClients: ({ filters }: GetClients) => {
    return api<TableDataResponse<IParentCompany>>({
      url: Endpoints.clientAdmin.clients.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        filter: {
          name: filters.name || undefined,
          status: filters.status,
          companyIds: filters.companies,
          activityDateFrom: filters.date?.from,
          activityDateTo: filters.date?.to,
        },
      },
    });
  },
  getClient: ({ clientId }: GetClient) => {
    return api<IParentCompany>({
      url: Endpoints.clientAdmin.clients.byId.root.replace(
        ':clientId',
        clientId,
      ),
    });
  },
  createClient: (data: CreateClient) => {
    return api({
      url: Endpoints.clientAdmin.clients.create,
      data,
      method: 'post',
    });
  },
  updateClient: ({ clientId, data }: UpdateClient) => {
    return api({
      url: Endpoints.clientAdmin.clients.byId.root.replace(
        ':clientId',
        clientId,
      ),
      data: {
        ...data,
        credit:
          typeof data.credit === 'number' ? toCents(data.credit) : undefined,
        lowDepositAlertThreshold:
          typeof data.lowDepositAlertThreshold === 'number'
            ? toCents(data.lowDepositAlertThreshold)
            : undefined,
      },
      method: 'patch',
    });
  },
  deleteClient: ({ clientId }: DeleteClient) => {
    return api({
      url: Endpoints.melpAdmin.clients.byId.replace(':clientId', clientId),
      method: 'delete',
    });
  },
  unlinkAppUsers: ({ clientId }: UnlinkAppUsers) => {
    return api<{ jobId: string }>({
      url: Endpoints.melpAdmin.clients.unlinkAppUsers.replace(
        ':clientId',
        clientId,
      ),
      method: 'post',
    });
  },
  getUnlinkAppUsersStatus: ({ clientId }: UnlinkAppUsers) => {
    return api<UnlinkAppUsersStatus>({
      url: Endpoints.melpAdmin.clients.unlinkAppUsers.replace(
        ':clientId',
        clientId,
      ),
    });
  },
};
